/**
 * Sender — отправка событий на бэкенд.
 *
 * Приоритет транспортов:
 *   1. navigator.sendBeacon  — fire-and-forget, переживает unload
 *   2. fetch с keepalive:true — fallback для старых браузеров
 *
 * Endpoint: /api/log/v (AdBlock-нейтральный, same-origin → проходит EasyPrivacy).
 */

import type { LogPayload } from './types';

const ENDPOINT_LOG = '/api/log/v';
const ENDPOINT_PING = '/api/log/ping';

/**
 * Возвращает базовый URL API.
 * В dev — http://localhost:5000, в prod — same-origin (`''`).
 */
function getApiBase(): string {
  if (typeof window === 'undefined') return '';
  // Same-origin в production: используем относительные URL
  // В dev фронт на 3000, бэк на 5000 — берём из env
  const envBase = process.env.NEXT_PUBLIC_API_URL;
  if (envBase && process.env.NODE_ENV !== 'production') {
    return envBase;
  }
  return '';
}

/**
 * Отправить событие на /api/log/v через sendBeacon (приоритет) или fetch keepalive.
 */
export function sendLog(payload: LogPayload): boolean {
  if (typeof window === 'undefined') return false;

  const url = getApiBase() + ENDPOINT_LOG;
  const body = JSON.stringify(payload);

  // ── sendBeacon — основной путь ──
  // Blob с Content-Type обязателен, иначе Express не распарсит JSON.
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return true;
    }
  } catch {
    // fallback ниже
  }

  // ── fetch keepalive — fallback ──
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',  // нужно для cookies
      mode: 'cors',
    }).catch(() => { /* swallow */ });
    return true;
  } catch {
    return false;
  }
}

/**
 * Получить identity (visitor_id + session_id) от сервера.
 * Используется при init() — один раз на маунте Providers.
 */
export async function pingIdentity(): Promise<{ vid: string; sid: string; isNew: boolean } | null> {
  if (typeof window === 'undefined') return null;

  try {
    const res = await fetch(getApiBase() + ENDPOINT_PING, {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as { vid: string; sid: string; isNew: boolean };
  } catch {
    return null;
  }
}
